/**
 * Toast Helper Utilities - SINGLE SOURCE OF TRUTH
 * 
 * This is the ONLY way to show toasts in the application.
 * All toast notifications MUST use showSuccess(), showError(), or showInfo() from this file.
 * 
 * High-level wrapper functions for toast notifications that automatically
 * extract messages from backend responses, ensuring clean error/success messages
 * are displayed instead of raw JSON.
 * 
 * Architecture:
 * - Adapter pattern: Adapts backend responses to toast service
 * - Message extraction: Handles various error/success response formats
 * - Clean API: Simple functions for common use cases
 * - Automatic deduplication via ToastService
 * 
 * @module ToastHelpers
 */

import { toastService } from './ToastService';
import { extractErrorMessage, extractErrorFromObject } from '../errorExtractor';

// When the session is force-terminated, in-flight/cancelled requests would otherwise
// spam error toasts ("Failed to load message", etc.) before the redirect to login.
let errorToastsSuppressed = false;

export const setErrorToastsSuppressed = (value) => {
  errorToastsSuppressed = value;
};

/**
 * Extract message from response data (for success messages from backend)
 * Reuses extractErrorMessage logic for consistency
 * @param {any} data - Response data that might contain message
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Extracted message
 */
const extractSuccessMessage = (data, defaultMessage) => {
  if (!data) return defaultMessage;
  if (typeof data === 'string') return data;
  // Reuse extractErrorMessage for consistency (it handles message, detail, data.message, etc.)
  return extractErrorMessage(data, defaultMessage);
};

/**
 * Show error toast with automatic message extraction
 * This function automatically extracts error messages from various error formats
 * and ensures only clean messages are shown, never raw JSON.
 * 
 * @param {string|object|Error} messageOrError - Error message string, error object, or error response data
 * @param {string} defaultMessage - Default message if extraction fails
 * @param {object} options - Toast options (duration, etc.)
 * 
 * @example
 * // Simple string message
 * showError('Something went wrong');
 * 
 * // Error object from axios
 * showError(error, 'Failed to load data');
 * 
 * // Error response data
 * showError(error.response.data, 'Operation failed');
 */
export const showError = (messageOrError, defaultMessage = 'An error occurred', options = {}) => {
  if (errorToastsSuppressed) return;

  // If it's a string, use it directly
  if (typeof messageOrError === 'string') {
    return toastService.error(messageOrError, options);
  }
  
  // Extract message from error object using centralized utility
  // This handles all error types consistently: axios errors, Error instances, error data objects
  const message = extractErrorFromObject(messageOrError, defaultMessage);
  
  // Use toast service with automatic deduplication
  return toastService.error(message, options);
};

/**
 * Show success toast with automatic message extraction
 * This function automatically extracts success messages from backend responses.
 * 
 * @param {string|object} messageOrData - Success message string or response data object
 * @param {string} defaultMessage - Default message if extraction fails
 * @param {object} options - Toast options (duration, etc.)
 * 
 * @example
 * // Simple string message
 * showSuccess('Operation completed');
 * 
 * // Response data from backend
 * showSuccess(response.data, 'Operation completed successfully');
 */
export const showSuccess = (messageOrData, defaultMessage = 'Operation completed successfully', options = {}) => {
  const message = typeof messageOrData === 'string' 
    ? messageOrData 
    : extractSuccessMessage(messageOrData, defaultMessage);
  return toastService.success(message, options);
};

/**
 * Show info toast with automatic message extraction
 * This function automatically extracts info messages from backend responses.
 * 
 * @param {string|object} messageOrData - Info message string or response data object
 * @param {string} defaultMessage - Default message if extraction fails
 * @param {object} options - Toast options (duration, etc.)
 * 
 * @example
 * // Simple string message
 * showInfo('User left the chat');
 * 
 * // Response data from backend
 * showInfo(response.data, 'Operation in progress');
 */
export const showInfo = (messageOrData, defaultMessage = '', options = {}) => {
  const message = typeof messageOrData === 'string' 
    ? messageOrData 
    : extractSuccessMessage(messageOrData, defaultMessage);
  return toastService.info(message, options);
};

export default {
  showError,
  showSuccess,
  showInfo
};

